import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { getBlockLibraryCliApiBaseUrl } from '@modules/firebase-core/services/BlockLibraryMutationApi';
import type { CliStrategyHeadWriteRequest } from '@modules/shared-types/src/strategyHeadWrite';

/** Canonical comparison for server JSON and client-converted Date/Timestamp readback. */
export function strategyHeadComparableJson(value: any): string {
  const normalize = (item: any): any => {
    if (item instanceof Date) return item.toISOString();
    if (item && typeof item.toDate === 'function') return item.toDate().toISOString();
    if (Array.isArray(item)) return item.map(normalize);
    if (item && typeof item === 'object') return Object.fromEntries(Object.keys(item).sort()
      .filter(key => item[key] !== undefined).map(key => [key, normalize(item[key])]));
    return item;
  };
  return JSON.stringify(normalize(value));
}

export async function saveStrategyHead(projectId: string, strategyId: string,
  request: CliStrategyHeadWriteRequest): Promise<Record<string, unknown>> {
  // Freeze the submitted packet before asynchronous capability/auth resolution.
  const body = JSON.stringify(request);
  const user = CoreFirebaseService.getInstance().auth.currentUser;
  if (!user) throw new Error('Sign in before saving a strategy.');
  const base = getBlockLibraryCliApiBaseUrl().replace(/\/$/, '');
  const capabilityResponse = await fetch(`${base}/api/cli/v1/capabilities`);
  const capabilities = await capabilityResponse.json();
  if (!capabilityResponse.ok || capabilities?.success === false
    || capabilities?.data?.features?.transactionalStrategyHeadWrites !== true) {
    throw new Error('The server does not support protected strategy saving yet. Update the server before retrying.');
  }
  const token = await user.getIdToken();
  const response = await fetch(`${base}/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/${encodeURIComponent(strategyId)}/head`, {
    method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body,
  });
  const payload = await response.json();
  if (!response.ok || payload?.success === false) throw new Error(payload?.error?.message ?? payload?.detail ?? 'Strategy save failed.');
  const receipt = payload?.data?.strategy;
  if (receipt?.projectId !== projectId || receipt?.id !== strategyId || receipt?.raw?.id !== strategyId
    || !receipt.raw || typeof receipt.raw !== 'object' || Array.isArray(receipt.raw)) {
    throw new Error(`Strategy ${strategyId} returned an invalid save receipt. Refresh before retrying.`);
  }
  return receipt.raw;
}
